// HVA session id — a local, standalone session identifier used to group
// engaged-user activity. Kept separate from Datadog RUM's session id so
// HVAs still flow from envs where RUM isn't initialized (e.g. staging).
// Persists in localStorage so the id is stable across tabs / reloads;
// rotates after 30 minutes of inactivity, matching the dashboard's
// engaged-session definition.

const STORAGE_KEY = "adora.hva.session";
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

type StoredSession = {
  id: string;
  lastActivity: number;
};

function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readStored(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as StoredSession).id === "string" &&
      typeof (parsed as StoredSession).lastActivity === "number"
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStored(session: StoredSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Privacy mode or quota errors — fine to ignore. The in-memory cache
    // below keeps the session id stable within the tab until storage
    // recovers or the tab reloads.
  }
}

// In-memory fallback for when localStorage reads/writes silently fail
// (quota exceeded, restrictive privacy mode, sandboxed iframe). Without
// this, each call would mint a fresh id since readStored() always
// returns null, defeating the whole point of a session id.
let cachedSession: StoredSession | null = null;

export function getHvaSessionId(): string | null {
  if (typeof window === "undefined") return null;

  const now = Date.now();
  // Prefer localStorage (canonical across tabs); fall back to the
  // in-memory cache when storage is unavailable.
  const stored = readStored() ?? cachedSession;
  const expired = stored === null || now - stored.lastActivity > INACTIVITY_TIMEOUT_MS;
  const id = expired ? generateId() : stored.id;
  const next: StoredSession = { id, lastActivity: now };
  cachedSession = next;
  writeStored(next);
  return id;
}
